import React, { useState } from 'react';

/**
 * Admin User Quota Manager Component
 * Allows admins to adjust user token quotas with full audit trail
 */
export const AdminUserQuotaManager = ({ users = [], onAdjustmentMade }) => {
  const [selectedUser, setSelectedUser] = useState(null);
  const [adjustmentData, setAdjustmentData] = useState({
    tokensToAdd: '',
    reason: '',
    isReset: false,
    adjustmentType: 'manual_increase'
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Ensure users is an array
  const usersList = Array.isArray(users) ? users : [];

  const filteredUsers = usersList.filter(u =>
    u.business_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setAdjustmentData({
      tokensToAdd: '',
      reason: '',
      isReset: false,
      adjustmentType: 'manual_increase'
    });
    setMessage('');
  };

  const handleAdjustmentChange = (e) => {
    const { name, value, type, checked } = e.target;
    setAdjustmentData({
      ...adjustmentData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmitAdjustment = async () => {
    if (!selectedUser) {
      setMessage('Please select a user');
      return;
    }

    if (!adjustmentData.reason.trim()) {
      setMessage('Please provide a reason for adjustment');
      return;
    }

    if (adjustmentData.isReset) {
      if (!window.confirm('Are you sure you want to RESET this user\'s quota?')) {
        return;
      }
    } else {
      const tokens = parseInt(adjustmentData.tokensToAdd);
      if (isNaN(tokens) || tokens === 0) {
        setMessage('Please enter a valid number of tokens');
        return;
      }
    }

    try {
      setLoading(true);
      const response = await fetch('/api/admin/llm/adjust-quota', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          userId: selectedUser.id,
          tokensToAdd: adjustmentData.isReset ? null : parseInt(adjustmentData.tokensToAdd),
          adjustmentType: adjustmentData.adjustmentType,
          reason: adjustmentData.reason,
          isReset: adjustmentData.isReset
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to adjust quota');
      }

      const result = await response.json();
      setMessage(`✓ Quota adjusted successfully. New balance: ${result.newBalance?.toLocaleString()} tokens`);
      
      if (onAdjustmentMade) {
        onAdjustmentMade();
      }

      // Reset form
      setSelectedUser(null);
      setAdjustmentData({
        tokensToAdd: '',
        reason: '',
        isReset: false,
        adjustmentType: 'manual_increase'
      });
    } catch (err) {
      setMessage(`✗ Error: ${err.message}`);
      console.error('Adjustment error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="quota-manager-container">
      <h3 className="text-lg font-semibold mb-4">Adjust User Token Quota</h3>

      <div className="manager-grid grid grid-cols-2 gap-4">
        {/* User Selection */}
        <div className="user-selection-panel border rounded-lg p-4">
          <h4 className="font-semibold mb-3">Select User</h4>
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 border rounded mb-3 text-sm"
          />

          <div className="user-list max-h-96 overflow-y-auto border rounded bg-gray-50">
            {filteredUsers.length > 0 ? (
              filteredUsers.map(user => (
                <div
                  key={user.id}
                  onClick={() => handleSelectUser(user)}
                  className={`p-3 border-b cursor-pointer hover:bg-blue-50 ${
                    selectedUser?.id === user.id ? 'bg-blue-100 border-l-4 border-l-blue-500' : ''
                  }`}
                >
                  <div className="font-medium text-sm">{user.business_name}</div>
                  <div className="text-xs text-gray-600">{user.email}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    {user.tokens_used_this_month?.toLocaleString() || 0} / {user.monthly_token_allowance?.toLocaleString() || 0} tokens
                  </div>
                </div>
              ))
            ) : (
              <div className="p-4 text-center text-gray-500 text-sm">No users found</div>
            )}
          </div>
        </div>

        {/* Adjustment Form */}
        <div className="adjustment-form-panel border rounded-lg p-4">
          <h4 className="font-semibold mb-3">Adjustment Details</h4>
          
          {selectedUser ? (
            <div className="space-y-3">
              <div className="selected-user bg-blue-50 p-3 rounded">
                <p className="text-sm font-medium">{selectedUser.business_name}</p>
                <p className="text-xs text-gray-600">{selectedUser.email}</p>
                <p className="text-xs text-gray-500 mt-1">
                  Current: {selectedUser.tokens_remaining?.toLocaleString() || 0} tokens remaining
                </p>
              </div>

              {/* Adjustment Type */}
              <div>
                <label className="block text-sm font-medium mb-2">Type</label>
                <select
                  name="adjustmentType"
                  value={adjustmentData.adjustmentType}
                  onChange={handleAdjustmentChange}
                  className="w-full px-3 py-2 border rounded text-sm"
                >
                  <option value="manual_increase">Increase Quota</option>
                  <option value="manual_decrease">Decrease Quota</option>
                  <option value="override">Override Limit</option>
                </select>
              </div>

              {/* Tokens Amount */}
              {!adjustmentData.isReset && (
                <div>
                  <label className="block text-sm font-medium mb-2">Tokens to Add</label>
                  <input
                    type="number"
                    name="tokensToAdd"
                    value={adjustmentData.tokensToAdd}
                    onChange={handleAdjustmentChange}
                    placeholder="Enter amount"
                    className="w-full px-3 py-2 border rounded text-sm"
                  />
                </div>
              )}

              {/* Reset Checkbox */}
              <label className="flex items-center space-x-2 text-sm">
                <input
                  type="checkbox"
                  name="isReset"
                  checked={adjustmentData.isReset}
                  onChange={handleAdjustmentChange}
                  className="rounded"
                />
                <span>Reset to monthly allowance</span>
              </label>

              {/* Reason */}
              <div>
                <label className="block text-sm font-medium mb-2">Reason (Required)</label>
                <textarea
                  name="reason"
                  value={adjustmentData.reason}
                  onChange={handleAdjustmentChange}
                  placeholder="Enter reason for adjustment..."
                  rows={3}
                  className="w-full px-3 py-2 border rounded text-sm"
                />
              </div>

              {/* Message */}
              {message && (
                <div className={`p-3 rounded text-sm ${
                  message.startsWith('✓') ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                }`}>
                  {message}
                </div>
              )}

              {/* Buttons */}
              <div className="flex gap-2 pt-2">
                <button
                  onClick={handleSubmitAdjustment}
                  disabled={loading}
                  className="flex-1 bg-blue-500 text-white px-3 py-2 rounded text-sm font-medium hover:bg-blue-600 disabled:opacity-50"
                >
                  {loading ? 'Adjusting...' : 'Apply Adjustment'}
                </button>
                <button
                  onClick={() => setSelectedUser(null)}
                  className="px-3 py-2 border rounded text-sm hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">
              <p className="text-sm">Select a user to adjust their quota</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminUserQuotaManager;
